import { Link } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";

export type AdminSection =
  | "dashboard"
  | "venues"
  | "categories"
  | "users"
  | "campaigns"
  | "ads"
  | "support";

interface AdminSidebarProps {
  active: AdminSection;
  onNavigate: (s: AdminSection) => void;
  collapsed: boolean;
  onToggle: () => void;
}

const NAV_ITEMS: { id: AdminSection; label: string; icon: string; badge?: number }[] = [
  { id: "dashboard",  label: "Dashboard",       icon: "ri-dashboard-3-line" },
  { id: "venues",     label: "Mekanlar",         icon: "ri-store-2-line",         badge: 36 },
  { id: "categories", label: "Kategoriler",      icon: "ri-layout-grid-line",     badge: 7 },
  { id: "users",      label: "Kullanıcılar",     icon: "ri-group-line",           badge: 8 },
  { id: "campaigns",  label: "Kampanyalar",      icon: "ri-coupon-line",          badge: 4 },
  { id: "ads",        label: "Reklamlar",        icon: "ri-advertisement-line",   badge: 4 },
  { id: "support",    label: "Destek Talepleri", icon: "ri-customer-service-2-line", badge: 3 },
];

export default function AdminSidebar({ active, onNavigate, collapsed, onToggle }: AdminSidebarProps) {
  const { user, logout } = useUser();

  const initials = user?.displayName
    ? user.displayName.split(" ").map((n) => n[0] || "").join("").toUpperCase().slice(0, 2)
    : "AD";

  return (
    <aside
      className={`flex flex-col bg-stone-900 text-white transition-all duration-300 flex-shrink-0 ${collapsed ? "w-16" : "w-60"} min-h-screen`}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-white/10 flex-shrink-0">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-700 flex-shrink-0">
          <i className="ri-compass-3-line text-white text-base" />
        </div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="text-sm font-bold leading-tight truncate">BursaGuide360</div>
            <div className="text-xs text-white/40 truncate">Admin Panel</div>
          </div>
        )}
        <button
          onClick={onToggle}
          className={`ml-auto w-6 h-6 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer flex-shrink-0 ${collapsed ? "hidden" : ""}`}
        >
          <i className="ri-sidebar-fold-line text-base" />
        </button>
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <button
          onClick={onToggle}
          className="flex items-center justify-center py-3 text-white/40 hover:text-white transition-colors cursor-pointer"
        >
          <i className="ri-sidebar-unfold-line text-lg" />
        </button>
      )}

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = active === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              title={collapsed ? item.label : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap group ${
                isActive
                  ? "bg-emerald-700 text-white"
                  : "text-white/60 hover:text-white hover:bg-white/10"
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-base`} />
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                      isActive ? "bg-white/20 text-white" : "bg-white/10 text-white/50"
                    }`}>
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom: Go to site */}
      <div className="p-3 border-t border-white/10">
        <Link
          to="/"
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap ${collapsed ? "justify-center" : ""}`}
        >
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-external-link-line text-base" />
          </span>
          {!collapsed && <span>Siteyi Görüntüle</span>}
        </Link>
        <button
          onClick={logout}
          className={`w-full flex items-center gap-3 px-3 py-2 mt-1 rounded-xl text-white/40 hover:text-red-400 hover:bg-white/5 transition-colors cursor-pointer ${collapsed ? "justify-center" : ""}`}
        >
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-logout-box-r-line text-base" />
          </span>
          {!collapsed && <span className="text-sm">Çıkış Yap</span>}
        </button>
        <div className={`flex items-center gap-2 px-3 py-2 mt-1 ${collapsed ? "justify-center" : ""}`}>
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.displayName}
              className="w-7 h-7 rounded-full object-cover object-top flex-shrink-0"
            />
          ) : (
            <div className="w-7 h-7 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {initials}
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0">
              <div className="text-xs font-medium text-white truncate">
                {user?.displayName || "Yönetici"}
              </div>
              <div className="text-xs text-white/40 truncate">
                {user?.email || "admin@bursaguide.com"}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
